<template>
  <div>
    <CSpinner v-if="isLoadingWatermarks" class="my-3" color="primary" />
    <div v-else>
      <textarea
        id="smsTemplateTextarea"
        v-model="messageText"
        rows="5"
        style="resize: none"
        ref="textarea"
        class="form-control text-gray-600 font-20"
        :disabled="disabled"
        :class="{ 'is-invalid': isMessageInvalid }"
        @input="(evt) => emitMessageChanged(evt.target.value)"
      ></textarea>
      <p v-if="isPlanFree" class="text-end text-gray-500 my-1">{{ $t('templates.sms.label.watermark-label') }}: {{ watermark }}</p>
      <span class="text-danger" style="font-size: 80%" v-if="isMessageInvalid">
        {{ messageInvalidFeedback }}
      </span>
      <CAlert v-if="isPlanFree" class="my-2" color="info">
        <div class="d-flex align-items-baseline">
          <div>
            <CIcon name="infoCircle" size="custom-size" class="mr-2" :height="16" style="transform: rotate(180deg)" viewBox="0 0 32 32" />
          </div>
          <p class="mb-0">
            {{ $t('templates.sms.label.watermark') }}
          </p>
        </div>
      </CAlert>
      <div>
        <CRow class="p-2 mt-2" :gutters="false">
          <CCol sm="6">
            <div class="text-gray-600 font-9 mb-1 text-uppercase">
              {{ $t(`templates.sms.label.N-CHARACTERS`) }}
            </div>
            <div class="text-gray-600 font-12 font-weight-bold">
              <span class="text-gray-700">{{ charsUsed }}</span> / {{ smsCharsPerContactTotal }}
            </div>
          </CCol>
          <CCol sm="6" class="pl-2">
            <div class="text-gray-600 font-9 mb-1 text-uppercase">
              {{ $t(`templates.sms.label.CHARACTERS-AVAILABLE`) }}
            </div>
            <div class="text-gray-600 font-12">
              <span class="font-weight-bold" :class="{ 'text-danger': isCharsAvailableNegative }">{{ charsAvailable }}</span>
            </div>
          </CCol>
        </CRow>
        <CRow class="p-2" :gutters="false">
          <CCol sm="6">
            <div class="text-gray-600 font-9 mb-1 text-uppercase">
              {{ $t('templates.sms.label.SMS-PER-CONTACT') }}
            </div>
            <div class="text-gray-600 font-12">
              <span class="text-gray-700 font-weight-bold">{{ smsPerContact }}</span>
            </div>
          </CCol>
          <CCol sm="6" class="pl-2">
            <div class="text-gray-600 font-9 mb-1 text-uppercase">
              {{ $t('templates.sms.label.CODIFICATION') }}
            </div>
            <div class="text-gray-600 font-12">
              <span class="text-gray-700 font-weight-bold">{{ unicodeType }}</span>
            </div>
          </CCol>
        </CRow>
      </div>
    </div>
  </div>
</template>

<script>
import services from '@/services.js'

const UNICODE_TYPES = {
  GSM_7_BIT: 'GSM 7 - bit',
  UCS_16_BIT: 'UCS2 - 16 bit',
}

const SMS_CHARS_DEFAULT_GSM = 160
const SMS_CHARS_DEFAULT_UCS = 70

export default {
  name: 'SMSTemplateTextarea',
  props: {
    isMessageInvalid: {
      type: Boolean,
      default: false,
    },
    messageInvalidFeedback: {
      type: String,
    },
    loadedMessageText: {
      type: String,
    },
    addedCustomField: {
      type: Object,
      default: () => {},
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      messageText: this.loadedMessageText || '',
      smsPerContact: 1,
      smsChars: SMS_CHARS_DEFAULT_GSM,
      charsUsed: 0,
      unicodeType: UNICODE_TYPES.GSM_7_BIT,
      gsm7BitChars: [
        '@',
        '£',
        '$',
        '¥',
        'è',
        'é',
        'ù',
        'ì',
        'ò',
        'Ç',
        '\n',
        'Ø',
        'ø',
        '\r',
        'Å',
        'å',
        'Δ',
        '_',
        'Φ',
        'Γ',
        'Λ',
        'Ω',
        'Π',
        'Ψ',
        'Σ',
        'Θ',
        'Ξ',
        'Æ',
        'æ',
        'ß',
        'É',
        ' ',
        '!',
        '"',
        '#',
        '¤',
        '%',
        '&',
        "'",
        '(',
        ')',
        '*',
        '+',
        ',',
        '-',
        '.',
        '/',
        '0',
        '1',
        '2',
        '3',
        '4',
        '5',
        '6',
        '7',
        '8',
        '9',
        ':',
        ';',
        '<',
        '=',
        '>',
        '?',
        '¡',
        'A',
        'B',
        'C',
        'D',
        'E',
        'F',
        'G',
        'H',
        'I',
        'J',
        'K',
        'L',
        'M',
        'N',
        'O',
        'P',
        'Q',
        'R',
        'S',
        'T',
        'U',
        'V',
        'W',
        'X',
        'Y',
        'Z',
        'Ä',
        'Ö',
        'Ñ',
        'Ü',
        '§',
        '¿',
        'a',
        'b',
        'c',
        'd',
        'e',
        'f',
        'g',
        'h',
        'i',
        'j',
        'k',
        'l',
        'm',
        'n',
        'o',
        'p',
        'q',
        'r',
        's',
        't',
        'u',
        'v',
        'w',
        'x',
        'y',
        'z',
        'ä',
        'ö',
        'ñ',
        'ü',
        'à',
      ],
      gsm7BitExtendedChars: ['\f', '^', '{', '}', '\\', '[', '~', ']', '|', '€'],
      isLoadingWatermarks: false,
      watermark: undefined,
      smsWatermarkCharCount: 0,
    }
  },
  mounted() {
    if (this.isPlanFree) this.getCampaignChannelsWatermarks()
    if (this.loadedMessageText) this.handleSMSCodification()
  },
  computed: {
    isPlanFree() {
      return this.planName.toLowerCase() === 'free' || false // Necessário comparação por nome devido uSendit também ser do tipo "free"
    },
    planName() {
      return this.$store.state.auth.session.organization.planName || ''
    },
    smsCharsPerContactTotal() {
      return this.smsChars * this.smsPerContact
    },
    maxCharsAvailable() {
      return this.unicodeType === UNICODE_TYPES.GSM_7_BIT ? 612 : 536
    },
    charsAvailable() {
      return this.maxCharsAvailable - this.charsUsed
    },
    isCharsAvailableNegative() {
      return Math.sign(this.charsAvailable) === -1
    },
  },
  methods: {
    clearText() {
      this.messageText = null
    },
    isGSM7Bit(char) {
      return this.gsm7BitChars.includes(char) || this.gsm7BitExtendedChars.includes(char)
    },
    isGSM7BitExtended(char) {
      return this.gsm7BitExtendedChars.includes(char)
    },
    emitMessageChanged(messageValue) {
      this.$nextTick(() => {
        this.$emit('message-changed', messageValue, this.isCharsAvailableNegative)
      })
    },
    handleSMSCodification() {
      const messageTextCharsArray = this.messageText.split('')
      let charsUsed = 0
      let isGSM7Bit = true

      messageTextCharsArray.forEach((char) => {
        charsUsed += 1
        if (this.isGSM7BitExtended(char)) {
          charsUsed += 1
        }
        if (!this.isGSM7Bit(char)) {
          isGSM7Bit = false
        }
      })

      this.unicodeType = isGSM7Bit ? UNICODE_TYPES.GSM_7_BIT : UNICODE_TYPES.UCS_16_BIT

      if (this.unicodeType == UNICODE_TYPES.UCS_16_BIT) {
        charsUsed = messageTextCharsArray.length
      }

      if (this.isPlanFree && !this.messageText.includes(this.watermark)) {
        charsUsed = charsUsed + this.smsWatermarkCharCount
      }

      const handleSMSCalculation = (maxSegments, defaultChars) => {
        if (charsUsed > defaultChars) {
          this.smsPerContact = Math.ceil((charsUsed / this.maxCharsAvailable) * maxSegments)
          if (this.smsPerContact > maxSegments) {
            this.smsPerContact = maxSegments
          }
          this.smsChars = this.maxCharsAvailable / maxSegments
        } else {
          this.smsChars = defaultChars
          this.smsPerContact = 1
        }
      }

      if (this.unicodeType === UNICODE_TYPES.GSM_7_BIT) {
        handleSMSCalculation(4, SMS_CHARS_DEFAULT_GSM)
      }

      if (this.unicodeType === UNICODE_TYPES.UCS_16_BIT) {
        handleSMSCalculation(8, SMS_CHARS_DEFAULT_UCS)
      }

      this.charsUsed = charsUsed
    },
    handleAddCustomField() {
      if (!this.addedCustomField) return

      document.getElementById('smsTemplateTextarea')?.focus()

      if (!this.messageText) {
        this.messageText = '[' + this.addedCustomField.personalizationTag + ']'
      } else {
        this.messageText += '[' + this.addedCustomField.personalizationTag + ']'
      }

      this.emitMessageChanged(this.messageText)
    },
    async getCampaignChannelsWatermarks() {
      try {
        this.isLoadingWatermarks = true
        const organizationId = this.$route.params.organizationId
        const response = await services.getCampaignChannelsWatermarks(organizationId)
        const data = response.data.data

        if (this.messageText.includes(data.smsWatermark)) {
          this.messageText.replace(data.smsWatermark, '')
          this.emitMessageChanged(this.messageText)
        }

        this.charsUsed = data.smsWatermarkCharCount
        this.smsWatermarkCharCount = data.smsWatermarkCharCount
        this.watermark = data.smsWatermark
      } finally {
        this.isLoadingWatermarks = false
        this.handleSMSCodification()
      }
    },
  },
  watch: {
    loadedMessageText: {
      handler(value) {
        this.messageText = value
      },
    },
    messageText: {
      handler() {
        this.handleSMSCodification()
      },
    },
    addedCustomField: {
      handler() {
        this.handleAddCustomField()
      },
    },
  },
}
</script>
